
import Vue from "vue"
import axios, { AxiosError, AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import WorkerPositionsTab from './WorkerPositionsTab.vue'

interface Client {
  id: number
  name: string
  positions?: Position[]
}

interface Position {
  id: number
  name: string
  priority: number | null
  totalDelegated: number
  campaign_count: number
  answer_count_yes: number
  answer_count_no: number,
  score: number,
  distance: number | null
}

interface Response {
  clients: Client[]
  clients_unmatch: Client[]
  clients_ban: Client[]
  favorite_jobs: string[]
  address_position: AddressPosition
  max_work_distance: number
}

interface Match {
  name: string
  positions: Position[]
}

interface AddressPosition {
  latitude: number
  longitude: number
}

export default Vue.extend({
  name: 'WorkerPositions',
  components: {
    WorkerPositionsTab
  },
  props: {
    id: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      clients: [] as Client[],
      banPositions: [] as Client[],
      unmatchPositions: [] as Client[],
      favorite_jobs: [] as string[],
      matches: [] as Match[],
      activeTab: 0,
      headers: [
        {text: this.$vuetify.lang.t('$vuetify.position') + ' / ' + this.$vuetify.lang.t('$vuetify.client'), value: 'name', width: '117px', align: 'start'},
        {text: this.$vuetify.lang.t('$vuetify.priority'), value: 'priority', align: 'center', class: 'centered-cell', width: '91px'},
        {text: this.$vuetify.lang.t('$vuetify.total_delegated'), value: 'totalDelegated', align: 'center', class: 'centered-cell', width: '105px'},
        {text: this.$vuetify.lang.t('$vuetify.propositions'), value: 'campaign_count', align: 'center', class: 'centered-cell', width: '119px'},
        {text: this.$vuetify.lang.t('$vuetify.yes'), value: 'answer_count_yes', align: 'center', class: 'centered-cell', width: '70px'},
        {text: this.$vuetify.lang.t('$vuetify.no'), value: 'answer_count_no', align: 'center', class: 'centered-cell', width: '72px'},
        {text: this.$vuetify.lang.t('$vuetify.no_response'), value: 'answer_count_null', align: 'center', class: 'centered-cell', width: '124px'},
        {text: 'Actions', value: 'actions', sortable: false, align: 'center'}
      ],
      matchHeaders: [
        {text: this.$vuetify.lang.t('$vuetify.position') + ' / ' + this.$vuetify.lang.t('$vuetify.client'), value: 'name', width: '110px', align: 'start'},
        {text: this.$vuetify.lang.t('$vuetify.score'), value: 'score', align: 'center', class: 'centered-cell', width: '100px'},
        {text: this.$vuetify.lang.t('$vuetify.distance'), value: 'distance', align: 'center', class: 'centered-cell', width: '100px'},
        {text: 'Actions', value: 'actions', sortable: false, align: 'center'}
      ],
      selectedPositions: [] as any[],
      searchInput: '',
      loading: true,
      address_position: undefined as AddressPosition | undefined,
      max_work_distance: 25 as number
    }
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.getData()
      }
    },
    favorite_jobs: {
      immediate: true,
      handler() {
        if (this.favorite_jobs.length > 0) {
          this.getMatchingPositions()
        }
      }
    }
  },
  computed: {
    allAvailablePositions(): any[] {
      // Create a Set of IDs of already associated or not associated positions
      const matchPositions = new Set([
        ...(this.clients || []).flatMap(client => client.positions?.map(pos => pos.id) || []),
        ...(this.unmatchPositions || []).flatMap(client => client.positions?.map(pos => pos.id) || []),
        ...(this.banPositions || []).flatMap(client => client.positions?.map(pos => pos.id) || [])
      ]);

      // Create a flat array of all positions available
      return (this.matches || []).flatMap(match =>
        (match.positions || [])
          .filter(pos => !matchPositions.has(pos.id))
          .map(pos => ({
            name: match.name,
            positions: [{
              id: pos.id,
              name: pos.name,
              score: pos.score ?? 0,
              distance: pos.distance ?? null
            }]
          }))
      );
    },
    matchPositions(): any[] {
      // Filter positions with score >= 50 and sort them
      const highScorePositions = this.allAvailablePositions
        // Filter positions with score >= 50 and distance < max_work_distance
        // .filter(item => (item.positions[0].distance <= this.max_work_distance) && (item.positions[0].score ?? 0) >= 50)
        .filter(item => {
          const hasValidScore = (item.positions[0].score ?? 0) >= 50;
          // verify distance only if address_position is defined
          const hasValidDistance = !this.address_position || item.positions[0].distance <= this.max_work_distance;
          return hasValidScore && hasValidDistance;
        })
        .sort((a, b) => b.positions[0].score - a.positions[0].score);

      return highScorePositions;
    },
    autocompleteItems(): any[] {
      const items: any[] = [];

      // Create items with score < 50
      this.allAvailablePositions.forEach(client => {
        const position = client.positions[0];
        if (position.score < 50 || position.distance > this.max_work_distance) {
          let distance = ""
          if (position.distance !== null) {
            distance = `${position.distance} km (+${Math.round(position.distance - this.max_work_distance)} km)`
          }
          items.push({
            displayText: `${client.name} - ${position.name}`,
            value: {
              positionId: position.id,
              positionName: position.name,
              clientName: client.name,
              score: position.score,
              distance: distance
            }
          });
        }
      });

      const sortedItems = items.sort((a, b) => b.value.score - a.value.score);

      // Add the "Select all" / "Reset selection" option as the first element
      const allItemsSelected = this.areAllItemsSelected(sortedItems.map(item => item.value));
      const actionText = allItemsSelected 
        ? this.$vuetify.lang.t('$vuetify.reset_selection')
        : this.$vuetify.lang.t('$vuetify.select_all_positions');

      return [
        {
          displayText: `${actionText} (${sortedItems.length} ${this.$vuetify.lang.t('$vuetify.results')})`,
          value: {
            selectAll: true,
            deselectAll: allItemsSelected,
            allItems: sortedItems.map(item => item.value)
          },
          raw: 'select_all_action'
        },
        ...sortedItems
      ];
    },
  },
  methods: {
    getData() {
      axios
        .get(`/v1/workers/${this.id}?fields=favorite_jobs,clients,clients_unmatch,clients_ban,address_position,max_work_distance`)
        .then((response: AxiosResponse<Response>) => {
          if (response.data.clients) {
            this.clients = response.data.clients
            this.banPositions = response.data.clients_ban
            this.unmatchPositions = response.data.clients_unmatch
            this.favorite_jobs = response.data.favorite_jobs
            this.address_position = response.data.address_position
            this.max_work_distance = response.data.max_work_distance
          }
          if (this.favorite_jobs.length > 0) {
            this.getMatchingPositions()
          } else {
            this.matches = []
            this.loading = false
          }
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar', { axiosError: error })
        })
    },
    getMatchingPositions() {
      let distance_from_address_position = ""
      if (this.address_position) {
        distance_from_address_position = `&distance_from_address_position=${this.address_position.latitude},${this.address_position.longitude}`
      }
      axios
        .get(`/v1/clients/positions?fields=client_name,position_name&match=${this.favorite_jobs.toString()}${distance_from_address_position}`)
        .then((response: AxiosResponse<Match[]>) => {
            this.matches = Object.values(response.data.reduce((acc: any, curr: any) => {
              if (!acc[curr.client_name]) {
                acc[curr.client_name] = {
                  name: curr.client_name,
                  positions: []
                };
              }
              acc[curr.client_name].positions.push({
                id: curr.position_id,
                name: curr.position_name,
                score: curr.score ?? 0,
                distance: curr.address_distance
              });
              return acc;
            }, {}));
            this.loading = false
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar', { axiosError: error })
        })
    },
    onPositionsSelected(selectedValues: any[]) {
      if (selectedValues && selectedValues.length > 0) {
        // Verify if "Select all" / "Reset selection" has been chosen
        const toggleAllItem = selectedValues.find(item => item.selectAll);
        
        if (toggleAllItem) {
          if (toggleAllItem.deselectAll) {
            // Reset selection
            this.selectedPositions = [];
          } else {
            // Select all
            this.selectedPositions = toggleAllItem.allItems;
            
            // Emit events for all items
            toggleAllItem.allItems.forEach((selectedValue: any) => {
              EventBus.$emit('position-selected', {
                positionId: selectedValue.positionId,
                positionName: selectedValue.positionName,
                clientName: selectedValue.clientName,
                score: selectedValue.score
              });
            });
          }
        } else {
          // Normal behavior - emit events for the selected items
          selectedValues.forEach(selectedValue => {
            if (!selectedValue.selectAll) {
              EventBus.$emit('position-selected', {
                positionId: selectedValue.positionId,
                positionName: selectedValue.positionName,
                clientName: selectedValue.clientName,
                score: selectedValue.score
              });
            }
          });
        }
        
        // Optional: pass to the first tab to see the corresponding positions
        this.activeTab = 0;
      }
    },
    removeSelection(valueToRemove: any) {
      this.selectedPositions = this.selectedPositions.filter(
        item => item.positionId !== valueToRemove.positionId
      );
    },
    isSelected(value: any): boolean {
      return this.selectedPositions.some(
        item => item.positionId === value.positionId
      );
    },
    addSelectedPositions(ban = false) {
      if (this.selectedPositions.length === 0) {
        return;
      }

      const positionIds = this.selectedPositions.map(item => item.positionId);
      let payload;
      if (ban) {
        payload = {
          [this.id]: { 
            add_ban: positionIds,
          }
        };
      } else {
        payload = {
          [this.id]: { 
            add_positions: positionIds,
          }
        };
      }

      axios
        .put('/v1/workers', payload)
        .then(() => {
          EventBus.$emit('snackbar', {
            message: ban ? this.$vuetify.lang.t('$vuetify.add_ban_success') : this.$vuetify.lang.t('$vuetify.add_positions_success'),
            color: 'success'
          });
          
          // Clear the selection after successful addition
          this.selectedPositions = [];
          
          // Refresh the data
          this.getData();
          
          // Optional: pass to the second tab to see the added positions
          this.activeTab = ban ? 2 : 1;
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar', { axiosError: error });
        });
    },
    areAllItemsSelected(allItems: any[]): boolean {
      if (allItems.length === 0) return false;
      return allItems.every(item => 
        this.selectedPositions.some(selected => 
          selected.positionId === item.positionId
        )
      );
    },
    customFilter(item: any, queryText: string, itemText: string) {
      // Always display the "Select all" / "Reset selection" option
      if (item.raw === 'select_all_action') {
        return true;
      }
      
      // For other elements, use the standard filtering
      const text = itemText.toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    }
  }
})
